import { faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ITableMultiselect } from "userful-chronos-app-common-js/dist/models/table/userfulTableInterface";

import "./TableMultiselect.scss";
import { Button, Checkbox, Dropdown, IDropdownItem, TOptionValue } from "userful-chronos-common-ui/dist/elements";

interface IProps {
    multiselect?: ITableMultiselect[];
    /**
     * Full list of available items (rows) to resolve IDs for default "All".
     * Accepts raw row objects or simple ID strings.
     */
    allItems?: any[];
    /**
     * Override for available IDs if you already have them resolved.
     */
    allIds?: string[];
    /**
     * Current selection. If omitted, component manages its own state.
     */
    selectedIds?: string[];
    onChange?: (selectedIDs: string[]) => void;
}

const normalizeIds = (items?: any[]): string[] => {
    if (!Array.isArray(items)) return [];
    const ids: string[] = [];

    const pushId = (value: unknown) => {
        if (value === null || value === undefined) return;
        const str = String(value);
        if (str) ids.push(str);
    };

    items.forEach((item, index) => {
        if (typeof item === "string" || typeof item === "number") {
            pushId(item);
            return;
        }
        if (item && typeof item === "object") {
            // Common shapes: { id }, { id: { value } }, { object: { id: { value } } }, { value }
            const idValue = (item as any).id;
            const nestedValue = (item as any)?.object?.id;
            if (typeof idValue === "string" || typeof idValue === "number") {
                pushId(idValue);
                return;
            }
            if (idValue && typeof idValue === "object" && "value" in idValue) {
                pushId((idValue as any).value);
                return;
            }
            if (nestedValue && (typeof nestedValue === "string" || typeof nestedValue === "number")) {
                pushId(nestedValue);
                return;
            }
            if (nestedValue && typeof nestedValue === "object" && "value" in nestedValue) {
                pushId((nestedValue as any).value);
                return;
            }
            if ((item as any).value !== undefined) {
                pushId((item as any).value);
                return;
            }
        }
        // Last resort fallback for missing IDs
        pushId(index);
    });

    return Array.from(new Set(ids));
};

export default function TableMultiselect({ multiselect, allItems, allIds, selectedIds, onChange }: IProps) {
    const { t } = useTranslation();

    const derivedAllIds = useMemo(() => {
        if (Array.isArray(allIds) && allIds.length > 0) {
            return normalizeIds(allIds);
        }
        return normalizeIds(allItems);
    }, [allIds, allItems]);

    const [internalSelection, setInternalSelection] = useState<string[]>(selectedIds ?? []);
    const controlled = Array.isArray(selectedIds);
    const effectiveSelection = controlled ? normalizeIds(selectedIds) : internalSelection;

    useEffect(() => {
        if (controlled) {
            setInternalSelection(normalizeIds(selectedIds));
        }
    }, [controlled, selectedIds]);

    const setSelection = useCallback(
        (next: string[]) => {
            const normalized = normalizeIds(next);
            if (!controlled) {
                setInternalSelection(normalized);
            }
            onChange?.(normalized);
        },
        [controlled, onChange],
    );

    const presets = useMemo(() => {
        if (!Array.isArray(multiselect)) return [];
        return multiselect.map((option) => {
            let ids: string[] = [];
            try {
                const result = option.logic ? option.logic(allItems ?? derivedAllIds) : [];
                ids = normalizeIds(result as any[]);
            } catch {
                ids = [];
            }
            return { option, ids };
        });
    }, [allItems, derivedAllIds, multiselect]);

    const dropdownItems: IDropdownItem[] = useMemo(() => {
        const items: IDropdownItem[] = [
            { id: "all", title: `${t("Shared.all")} (${derivedAllIds.length})` },
            { id: "none", title: `${t("Shared.none")} (0)` },
        ];

        presets.forEach(({ option, ids }) => {
            const label = option.title ? t(option.title) : option.id;
            items.push({
                id: option.id,
                title: `${label} (${ids.length})`,
            });
        });

        return items;
    }, [derivedAllIds.length, presets, t]);

    const selectionCount = effectiveSelection.length;
    const checkboxState: TOptionValue = useMemo(() => {
        if (!selectionCount) return "unselected";
        if (derivedAllIds.length > 0 && selectionCount === derivedAllIds.length) return "selected";
        return "indeterminate";
    }, [derivedAllIds.length, selectionCount]);

    const handleCheckboxClick = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            const allSelected = derivedAllIds.length > 0 && selectionCount === derivedAllIds.length;
            if (allSelected || checkboxState === "indeterminate") {
                setSelection([]);
            } else {
                setSelection(derivedAllIds);
            }
        },
        [checkboxState, derivedAllIds, selectionCount, setSelection],
    );

    const handleDropdownChange = useCallback(
        (item: IDropdownItem) => {
            if (item.id === "all") {
                setSelection(derivedAllIds);
                return;
            }
            if (item.id === "none") {
                setSelection([]);
                return;
            }
            const match = presets.find((preset) => preset.option.id === item.id);
            if (match) {
                setSelection(match.ids);
            }
        },
        [derivedAllIds, presets, setSelection],
    );

    return (
        <div className="userful-table-multiselect" onClick={(event) => event.stopPropagation()}>
            <Checkbox value={checkboxState} onClick={handleCheckboxClick} />
            <Dropdown
                items={dropdownItems}
                onChange={handleDropdownChange}
                trigger={
                    <Button variant="ghost" size="small" iconOnly aria-label={t("Shared.select")}>
                        <FontAwesomeIcon icon={faAngleDown} />
                    </Button>
                }
                usePortal
            />
        </div>
    );
}
